/** Card clip / group model on top of host overlay clips. */

import { layoutCardFrames } from './layouts';
import { staggerStartTimes } from './animation';
import { clampCarouselParams, DEFAULT_CAROUSEL_PARAMS } from './template';

export const OVERLAY_KIND_CARD = 'card';

export function isCardOverlayClip(clip) {
  return String(clip?.overlayKind || clip?.overlay_kind || '').toLowerCase() === OVERLAY_KIND_CARD
    || Boolean(clip?.carouselGroupId);
}

export function normalizeCardItem(raw, index = 0) {
  const src = raw && typeof raw === 'object' ? raw : {};
  const title = String(src.title || src.label || src.name || '').trim() || `Item ${index + 1}`;
  const number = String(src.number ?? src.index ?? index + 1).trim() || String(index + 1);
  return {
    id: String(src.id || `card-item-${index}`),
    number,
    title,
    imageUrl: String(src.imageUrl || src.image_url || src.preview_url || '').trim(),
    highlight: String(src.highlight || '').trim(),
  };
}

export function newCarouselGroupId() {
  return `carousel-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

export function buildCarouselClips({
  items = [],
  playhead = 0,
  aspectRatio = '9:16',
  params = {},
  groupId = '',
  holdDuration = 2.6,
} = {}) {
  const list = (Array.isArray(items) ? items : []).map((row, i) => normalizeCardItem(row, i));
  if (!list.length) return [];
  const cfg = clampCarouselParams({ ...DEFAULT_CAROUSEL_PARAMS, ...params });
  const gid = groupId || newCarouselGroupId();
  const frames = layoutCardFrames({
    count: list.length,
    layout: cfg.layout,
    aspectRatio,
    gap: cfg.gap,
    scale: cfg.scale,
  });
  const times = staggerStartTimes({
    playhead,
    count: list.length,
    staggerDelay: cfg.staggerDelay,
    holdDuration,
  });
  const visible = cfg.visibleCount > 0 ? cfg.visibleCount : list.length;
  return list.map((item, i) => {
    const frame = frames[i] || frames[frames.length - 1];
    const timed = times[i] || times[0];
    const hidden = i >= visible;
    return {
      id: `${gid}-${item.id}`,
      name: `${item.number}. ${item.title}`,
      overlayKind: OVERLAY_KIND_CARD,
      carouselGroupId: gid,
      carouselIndex: i,
      carouselParams: cfg,
      cardNumber: item.number,
      cardTitle: item.title,
      cardHighlight: item.highlight,
      imageUrl: item.imageUrl,
      startTime: timed.startTime,
      duration: timed.duration,
      opacity: hidden ? 0.001 : 1,
      muted: hidden,
      borderRadius: cfg.borderRadius,
      layer: {
        x: frame.x,
        y: frame.y,
        width: frame.width,
        height: frame.height,
        rotation: frame.rotation || 0,
        textAlign: 'center',
      },
    };
  });
}

export function clipsInCarouselGroup(tracks, groupId) {
  const gid = String(groupId || '');
  return (tracks || []).flat().filter((clip) => String(clip?.carouselGroupId || '') === gid)
    .sort((a, b) => (Number(a.carouselIndex) || 0) - (Number(b.carouselIndex) || 0));
}

export function relayoutCarouselGroup(tracks, groupId, nextParams, aspectRatio = '9:16') {
  const gid = String(groupId || '');
  const members = clipsInCarouselGroup(tracks, gid);
  if (!members.length) return tracks;
  const cfg = clampCarouselParams({ ...(members[0].carouselParams || {}), ...nextParams });
  const frames = layoutCardFrames({
    count: members.length,
    layout: cfg.layout,
    aspectRatio,
    gap: cfg.gap,
    scale: cfg.scale,
  });
  const visible = cfg.visibleCount > 0 ? cfg.visibleCount : members.length;
  const byId = new Map(members.map((clip, i) => {
    const frame = frames[i] || frames[frames.length - 1];
    const hidden = i >= visible;
    return [clip.id, {
      ...clip,
      carouselParams: cfg,
      muted: hidden,
      opacity: hidden ? 0.001 : (clip.opacity && clip.opacity > 0.05 ? clip.opacity : 1),
      borderRadius: cfg.borderRadius,
      layer: {
        ...(clip.layer || {}),
        x: frame.x,
        y: frame.y,
        width: frame.width,
        height: frame.height,
        rotation: frame.rotation || 0,
      },
    }];
  }));
  return (tracks || []).map((lane) => lane.map((clip) => byId.get(clip.id) || clip));
}

export function restaggerCarouselGroup(tracks, groupId, playhead, holdDuration) {
  const members = clipsInCarouselGroup(tracks, groupId);
  if (!members.length) return tracks;
  const delay = Number(members[0]?.carouselParams?.staggerDelay) || 0.25;
  const times = staggerStartTimes({
    playhead: playhead ?? Math.min(...members.map((c) => Number(c.startTime) || 0)),
    count: members.length,
    staggerDelay: delay,
    holdDuration: holdDuration || Number(members[0].duration) || 2.6,
  });
  const byId = new Map(members.map((clip, i) => [clip.id, {
    ...clip,
    startTime: times[i].startTime,
    duration: times[i].duration,
  }]));
  return (tracks || []).map((lane) => lane.map((clip) => byId.get(clip.id) || clip));
}

export function patchCarouselClip(tracks, clipId, patch) {
  return (tracks || []).map((lane) => lane.map((clip) => (clip.id === clipId ? { ...clip, ...patch } : clip)));
}

export function removeCarouselGroup(tracks, groupId) {
  const gid = String(groupId || '');
  return (tracks || []).map((lane) => lane.filter((clip) => String(clip?.carouselGroupId || '') !== gid));
}

export function applyParamsToGroup(tracks, groupId, params, aspectRatio = '9:16') {
  let next = relayoutCarouselGroup(tracks, groupId, params, aspectRatio);
  const members = clipsInCarouselGroup(next, groupId);
  if (!members.length) return next;
  const start = Math.min(...members.map((c) => Number(c.startTime) || 0));
  next = restaggerCarouselGroup(next, groupId, start, Number(members[0].duration) || 2.6);
  return next;
}
